import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, X, Loader2, MessageCircle, FolderPlus, ShieldAlert, FileText, AlertTriangle } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import {
  listAdminBookings,
  getAdminBooking,
  updateBookingStatus,
  verifyBookingPayment,
  updateBookingDetails,
  rejectBookingReceipt,
} from "@/lib/admin.functions";
import { createCase } from "@/lib/clinic.functions";
import { getMyRole } from "@/lib/dr-sara.functions";
import { RouteNotFound } from "@/components/route-boundaries";
import { IntakeAnswersView } from "@/components/intake-answers-view";
import { getPublicSiteSettings } from "@/lib/public.functions";
import { paymentMethodLabel, type SessionMode } from "@/lib/booking-payment";
import { preferredTimesSummary } from "@/lib/preferred-times";
import { buildAppointmentConfirmMessage, timeZoneForCountry } from "@/lib/booking-whatsapp";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { ProgramCard } from "@/components/admin/program-card";

const listQ = () => queryOptions({ queryKey: ["admin", "bookings"], queryFn: () => listAdminBookings() });
const settingsQ = () =>
  queryOptions({ queryKey: ["public", "site_settings"], queryFn: () => getPublicSiteSettings() });

const PACKAGE_SLUG = "monthly-package-4";

function bookingPlace(b: any, clinicAddress: string) {
  return b.session_mode === "in_person" ? `في العيادة — ${clinicAddress}` : "أونلاين";
}


export const Route = createFileRoute("/_authenticated/admin/bookings")({
  head: () => ({ meta: [
    { title: "لوحة الحجوزات | إدارة د. سارة النحّاس" },
    { name: "description", content: "إدارة حجوزات الاستشارة وبرامج ما بعد الاستشارة لفريق د. سارة النحّاس." },
    { property: "og:title", content: "لوحة الحجوزات | إدارة د. سارة النحّاس" },
    { property: "og:description", content: "إدارة حجوزات الاستشارة وبرامج ما بعد الاستشارة لفريق د. سارة النحّاس." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ()),
  errorComponent: ({ error }) => <p className="p-6 text-muted-foreground">{String(error)}</p>,
  notFoundComponent: RouteNotFound,
  component: BookingsPage,
});

function BookingsPage() {
  const { data } = useSuspenseQuery(listQ());
  const { data: settings } = useQuery(settingsQ());
  const clinicAddress = settings?.clinic_address_ar || "أبو رمانة، دمشق";
  const [openId, setOpenId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [onlyUnscheduled, setOnlyUnscheduled] = useState(false);
  const qc = useQueryClient();
  const updateStatus = useServerFn(updateBookingStatus);

  const unscheduledCount = data.filter((b: any) => !b.scheduled_at).length;
  const filtered = data.filter((b: any) => {
    if (onlyUnscheduled && b.scheduled_at) return false;
    if (!q) return true;
    const hay = `${b.client?.full_name ?? ""} ${b.client?.phone_whatsapp ?? ""} ${b.service?.title_ar ?? ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  async function move(id: string, from: string, to: string) {
    if (from === to) return;
    qc.setQueryData(["admin", "bookings"], (old: any) =>
      (old ?? []).map((b: any) => (b.id === id ? { ...b, status: to } : b)),
    );
    try {
      await updateStatus({ data: { id, status: to as any } });
      toast.success("تم تحديث الحالة", {
        action: {
          label: "تراجع",
          onClick: async () => {
            qc.setQueryData(["admin", "bookings"], (old: any) =>
              (old ?? []).map((b: any) => (b.id === id ? { ...b, status: from } : b)),
            );
            await updateStatus({ data: { id, status: from as any } });
          },
        },
      });
    } catch (e: any) {
      qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
      toast.error(e?.message ?? "فشل التحديث");
    }
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="kicker text-modern-orange">إدارة الحجوزات</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-foreground">لوحة الحجوزات</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setOnlyUnscheduled((v) => !v)}
            aria-pressed={onlyUnscheduled}
            className={`rounded-full border px-4 py-2 text-sm font-bold transition-colors ${
              onlyUnscheduled
                ? "border-modern-orange bg-modern-orange/20 text-foreground"
                : "border-input bg-card text-muted-foreground hover:text-foreground"
            }`}
          >
            بانتظار تثبيت الموعد ({unscheduledCount})
          </button>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="بحث بالاسم أو الهاتف..."
            className="w-full rounded-full border border-input bg-card px-4 py-2 text-sm outline-none focus:border-ring sm:w-64"
          />
        </div>
      </header>

      {filtered.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-card/60 p-10 text-center text-muted-foreground">لا شيء</p>
      ) : (
        <div className="space-y-3">
          {[...filtered]
            .sort((a: any, b: any) => String(b.created_at).localeCompare(String(a.created_at)))
            .map((b: any) => (
              <BookingCard
                key={b.id}
                b={b}
                clinicAddress={clinicAddress}
                onOpen={() => setOpenId(b.id)}
                onMove={(to) => move(b.id, b.status, to)}
              />
            ))}
        </div>
      )}

      {openId && <DetailDrawer id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function BookingCard({
  b,
  clinicAddress,
  onOpen,
  onMove,
}: {
  b: any;
  clinicAddress: string;
  onOpen: () => void;
  onMove: (to: string) => void;
}) {
  const awaitingVerification = b.payment_status === "receipt_uploaded";
  const missingMeetingInfo = b.status === "confirmed" && (!b.meeting_link || !b.scheduled_at);
  const isPackage = b.service?.slug === PACKAGE_SLUG;
  const times = preferredTimesSummary(b.preferred_times);
  const isConfirmed = b.status === "confirmed";
  const waHref = b.client?.phone_whatsapp
    ? `https://wa.me/${String(b.client.phone_whatsapp).replace(/\D/g, "")}`
    : null;

  return (
    <article className="rounded-xl bg-background p-4 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-base font-bold text-foreground">{b.client?.full_name ?? "بلا اسم"}</p>
          <p className="mt-0.5 text-xs text-muted-foreground" dir="ltr">{b.client?.phone_whatsapp ?? ""}</p>
          {(b.client?.country || b.client?.city) && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {b.client?.country ?? "—"}{b.client?.city ? ` — ${b.client.city}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {isPackage && (
            <span className="inline-flex rounded-full bg-soft-blue/30 px-2 py-0.5 text-[11px] font-bold text-foreground">
              باقة 4 جلسات
            </span>
          )}
          {!b.scheduled_at && (
            <span className="inline-flex items-center gap-1 rounded-full bg-modern-orange/25 px-2 py-0.5 text-[11px] font-bold text-foreground">
              <AlertTriangle className="h-3 w-3" /> بانتظار تثبيت الموعد
            </span>
          )}
          {awaitingVerification && (
            <span className="inline-flex items-center gap-1 rounded-full bg-modern-orange/20 px-2 py-0.5 text-[11px] font-bold text-foreground">
              <FileText className="h-3 w-3" /> إيصال مرفوع
            </span>
          )}
          {b.receipt_rejection_reason && (
            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[11px] font-bold text-destructive">
              <ShieldAlert className="h-3 w-3" /> إيصال مرفوض
            </span>
          )}
          {missingMeetingInfo && (
            <span className="inline-flex items-center gap-1 rounded-full bg-modern-orange/25 px-2 py-0.5 text-[11px] font-bold text-foreground">
              <AlertTriangle className="h-3 w-3" /> مؤكد بدون رابط جلسة
            </span>
          )}
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-muted-foreground">الخدمة</dt>
          <dd className="font-semibold text-foreground">{b.service?.title_ar ?? "—"}</dd>
          {b.service?.price_usd != null && (
            <dd className="text-muted-foreground">${b.service.price_usd} · {b.service.duration_min} دقيقة</dd>
          )}
        </div>
        <div>
          <dt className="text-muted-foreground">المكان</dt>
          <dd className="font-semibold text-foreground">{bookingPlace(b, clinicAddress)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">الدفع</dt>
          <dd className="font-semibold text-foreground">{paymentMethodLabel(b.payment_method)}</dd>
          <dd className="text-muted-foreground">{b.payment_status}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{b.scheduled_at ? "الموعد" : "أُرسل"}</dt>
          <dd className="font-semibold text-foreground">
            {b.scheduled_at
              ? new Date(b.scheduled_at).toLocaleString("ar-u-nu-latn", { dateStyle: "medium", timeStyle: "short" })
              : new Date(b.created_at).toLocaleDateString("ar-u-nu-latn")}
          </dd>
        </div>
      </dl>
      {times && <p className="mt-2 text-xs text-muted-foreground">الأوقات المناسبة: {times}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {waHref && (
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-green-100 px-3 py-1.5 text-[13px] font-bold text-green-800 hover:opacity-90"
          >
            <MessageCircle className="h-3.5 w-3.5" /> واتساب
          </a>
        )}
        <button
          onClick={() => onMove("confirmed")}
          disabled={isConfirmed}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors ${
            isConfirmed
              ? "bg-soft-green/40 text-foreground"
              : "border border-input bg-card text-muted-foreground hover:bg-soft-green/30 hover:text-foreground"
          }`}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> تأكيد
        </button>
        <button
          onClick={() => onMove("new")}
          disabled={!isConfirmed}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors ${
            !isConfirmed
              ? "bg-muted text-foreground"
              : "border border-input bg-card text-muted-foreground hover:bg-accent/20 hover:text-foreground"
          }`}
        >
          غير مؤكد
        </button>
        <button
          onClick={onOpen}
          className="rounded-full border border-input bg-card px-3 py-1.5 text-[11px] font-bold text-foreground hover:bg-accent/20"
        >
          فتح التفاصيل
        </button>
      </div>
    </article>
  );
}

function DetailDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = queryOptions({ queryKey: ["admin", "booking", id], queryFn: () => getAdminBooking({ data: { id } }) });
  const { data, isLoading } = useQuery(q);
  const { data: role } = useQuery({ queryKey: ["me", "role"], queryFn: () => getMyRole() });
  const qc = useQueryClient();
  const verifyFn = useServerFn(verifyBookingPayment);
  const rejectFn = useServerFn(rejectBookingReceipt);
  const updateFn = useServerFn(updateBookingDetails);
  const createCaseFn = useServerFn(createCase);
  const navigate = useNavigate();
  const [scheduled, setScheduled] = useState("");
  const [meeting, setMeeting] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const b = data?.booking;
  const svc = data?.service;
  const client = data?.client;
  const { data: settings } = useQuery(settingsQ());
  const clinicAddress = settings?.clinic_address_ar || "أبو رمانة، دمشق";
  const confirmWaHref =
    b?.scheduled_at && client?.phone_whatsapp
      ? toWhatsAppLink(
          client.phone_whatsapp,
          buildAppointmentConfirmMessage({
            fullName: client.full_name ?? "",
            serviceTitle: svc?.title_ar ?? "الاستشارة",
            scheduledAt: b.scheduled_at,
            sessionMode: (b.session_mode as SessionMode) ?? "online",
            clinicAddress,
            paymentLabel: paymentMethodLabel(b.payment_method),
            needsReceipt:
              b.payment_method !== "cash_at_clinic" && b.payment_status !== "verified",
            confirmUrl:
              typeof window !== "undefined" ? `${window.location.origin}/book/confirm/${b.id}` : null,
            clientTimeZone: timeZoneForCountry(client.country),
          }),
        )
      : "";

  async function saveDetails() {
    if (!b) return;
    setBusy(true);
    try {
      await updateFn({ data: { id: b.id, scheduled_at: scheduled || null, meeting_link: meeting || null, admin_notes: notes || null } });
      toast.success("تم الحفظ");
      qc.invalidateQueries({ queryKey: ["admin", "booking", id] });
      qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
    } catch (e: any) { toast.error(e?.message ?? "فشل الحفظ"); } finally { setBusy(false); }
  }

  async function verify() {
    if (!b) return;
    if (!window.confirm("تأكيد الدفع؟ سيتحوّل الحجز إلى «مؤكّد» ويُنشأ سجل نشاط.")) return;
    setBusy(true);
    try {
      await verifyFn({ data: { id: b.id } });
      toast.success("تم تأكيد الدفع");
      qc.invalidateQueries({ queryKey: ["admin", "booking", id] });
      qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
    } catch (e: any) { toast.error(e?.message ?? "فشل التأكيد"); } finally { setBusy(false); }
  }

  async function reject() {
    if (!b) return;
    const reason = window.prompt("سبب رفض الإيصال (سيراه العميل):", "الإيصال غير واضح، الرجاء إعادة إرساله.");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try {
      await rejectFn({ data: { id: b.id, reason: reason.trim() } });
      toast.success("تم رفض الإيصال وإبلاغ العميل");
      qc.invalidateQueries({ queryKey: ["admin", "booking", id] });
      qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
    } catch (e: any) { toast.error(e?.message ?? "فشل"); } finally { setBusy(false); }
  }

  async function makeCase() {
    if (!b || !client) return;
    setBusy(true);
    try {
      const alias = window.prompt("اسم/كنية الطفل للحالة:", (client.full_name?.split(" ").slice(-1)[0] ?? "") + " (ابن/بنت)") ?? "";
      if (!alias.trim()) { setBusy(false); return; }
      const res = await createCaseFn({ data: { child_alias: alias.trim(), from_booking_id: b.id } });
      toast.success("تم إنشاء الحالة");
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      navigate({ to: "/admin/cases/$id", params: { id: res.id } });
    } catch (e: any) { toast.error(e?.message ?? "فشل"); } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-foreground/30 backdrop-blur-sm" />
      <aside onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto bg-card p-6 shadow-lift md:max-w-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold text-foreground">تفاصيل الحجز</h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-accent/20"><X className="h-5 w-5" /></button>
        </div>

        {isLoading || !b ? (
          <p className="mt-8 text-muted-foreground">تحميل...</p>
        ) : (
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl bg-background/70 p-4">
              <p className="font-display text-lg font-bold text-foreground">{client?.full_name}</p>
              <p className="text-sm text-muted-foreground">{client?.phone_whatsapp} · {client?.country ?? "—"} {client?.city ? `— ${client.city}` : ""}</p>
              <p className="mt-2 text-sm text-foreground">
                {svc?.title_ar} · ${svc?.price_usd} · {svc?.duration_min} دقيقة
                {svc?.slug === PACKAGE_SLUG && (
                  <span className="ms-2 inline-flex rounded-full bg-soft-blue/30 px-2 py-0.5 text-[11px] font-bold text-foreground">
                    باقة 4 جلسات
                  </span>
                )}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {bookingPlace(b, clinicAddress)} · الدفع: {paymentMethodLabel(b.payment_method)}
              </p>
              {preferredTimesSummary(b.preferred_times) && (
                <p className="mt-1 text-sm text-muted-foreground">
                  الأوقات المناسبة: {preferredTimesSummary(b.preferred_times)}
                </p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">حالة الحجز: {statusLabel(b.status)} · الدفع: {b.payment_status}</p>
              {!b.scheduled_at && (
                <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-modern-orange/25 px-2.5 py-0.5 text-[11px] font-bold text-foreground">
                  <AlertTriangle className="h-3 w-3" /> بانتظار تثبيت الموعد — أضف الموعد أدناه
                </p>
              )}
              {b.status === "confirmed" && !b.meeting_link && b.session_mode !== "in_person" && (
                <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-modern-orange/25 px-2.5 py-0.5 text-[11px] font-bold text-foreground">
                  <AlertTriangle className="h-3 w-3" /> مؤكد بدون رابط جلسة
                </p>
              )}
              {b.receipt_rejection_reason && (
                <p className="mt-2 rounded-lg bg-destructive/10 p-2 text-xs text-destructive">
                  سبب الرفض السابق: {b.receipt_rejection_reason}
                </p>
              )}
              {b.scheduled_at && confirmWaHref && (
                <a
                  href={confirmWaHref}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-100 px-5 py-2.5 font-display text-sm font-bold text-green-800 hover:opacity-90"
                >
                  <MessageCircle className="h-4 w-4" /> أرسل تأكيد الموعد عبر واتساب
                </a>
              )}
            </div>

            {data?.receiptUrl && (
              <div className="rounded-2xl bg-background/70 p-4">
                <p className="mb-2 font-display text-sm font-bold text-foreground">إيصال الدفع</p>
                <a href={data.receiptUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-modern-orange hover:underline">
                  فتح الإيصال <ExternalLink className="h-3.5 w-3.5" />
                </a>
                {b.payment_status !== "verified" && (
                  role?.isAdmin ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={verify} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-soft-green/40 px-4 py-2 font-display text-sm font-bold text-foreground hover:bg-soft-green/60 disabled:opacity-50">
                        <CheckCircle2 className="h-4 w-4" /> تأكيد الدفع
                      </button>
                      {b.payment_status === "receipt_uploaded" && (
                        <button onClick={reject} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-destructive/15 px-4 py-2 font-display text-sm font-bold text-destructive hover:bg-destructive/25 disabled:opacity-50">
                          <ShieldAlert className="h-4 w-4" /> رفض الإيصال
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-muted-foreground">تأكيد الدفع مقتصر على المدير.</p>
                  )
                )}
              </div>
            )}

            <div className="rounded-2xl bg-background/70 p-4">
              <p className="mb-3 font-display text-sm font-bold text-foreground">جدولة الجلسة</p>
              <div className="space-y-3">
                <label className="block text-sm">
                  <span className="mb-1 block text-muted-foreground">موعد الجلسة</span>
                  <input type="datetime-local" value={scheduled || (b.scheduled_at ? b.scheduled_at.slice(0, 16) : "")} onChange={(e) => setScheduled(e.target.value)} className="w-full rounded-xl border border-input bg-card px-3 py-2 outline-none" />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-muted-foreground">رابط الجلسة</span>
                  <input value={meeting || b.meeting_link || ""} onChange={(e) => setMeeting(e.target.value)} className="w-full rounded-xl border border-input bg-card px-3 py-2 outline-none" placeholder="https://..." />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-muted-foreground">ملاحظات داخلية</span>
                  <textarea value={notes || b.admin_notes || ""} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full rounded-xl border border-input bg-card px-3 py-2 outline-none" />
                </label>
                <button onClick={saveDetails} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover disabled:opacity-50">
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} حفظ
                </button>
              </div>
            </div>

            {role?.isAdmin && <ProgramCard bookingId={b.id} phone={client?.phone_whatsapp} childName={typeof data?.intakeAnswers === "object" && data.intakeAnswers && "child_name" in data.intakeAnswers ? String(data.intakeAnswers.child_name ?? "") : undefined} />}

            {data?.intakeAnswers && (
              <details className="rounded-2xl bg-background/70 p-4">
                <summary className="cursor-pointer font-display text-sm font-bold text-foreground">استمارة الاستشارة</summary>
                <div className="mt-3 max-h-[32rem] overflow-auto">
                  <IntakeAnswersView schema={(data as any).intakeSchema} answers={data.intakeAnswers} />
                </div>
              </details>
            )}

            <a href={`https://wa.me/${(client?.phone_whatsapp ?? "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-input bg-card px-5 py-2 font-display text-sm font-bold text-foreground hover:bg-accent/15">
              <MessageCircle className="h-4 w-4" /> راسليها على واتساب
            </a>
            {b.status === "confirmed" && (
              <button onClick={makeCase} disabled={busy} className="ms-2 inline-flex items-center gap-2 rounded-full bg-soft-green/40 px-5 py-2 font-display text-sm font-bold text-foreground hover:bg-soft-green/60 disabled:opacity-50">
                <FolderPlus className="h-4 w-4" /> أنشئ حالة من هذا الحجز
              </button>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function statusLabel(s: string) {
  const map: Record<string, string> = { new: "جديد", contacted: "تواصل", awaiting_payment: "بانتظار الدفع", confirmed: "مؤكّد", completed: "مكتمل", cancelled: "ملغى", no_show: "غياب" };
  return map[s] ?? s;
}

